import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { useLocation } from '@tanstack/react-router';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';

import { getOnboardingKnowledgeBaseLink } from '@/components/onboarding/onboardingKnowledgeBase';

import { getGuideHeading, getGuideIntro, getStrategyIdFromPath } from './guide.helpers';
import { useGuideContext } from './guide.services';
import { useGuideStore } from './guide.store';
import { GuideSuggestion } from './guide.types';

const knowledgeBaseSearch = 'https://weavrau.zendesk.com/hc/en-au/search?query=';

const WeavrGuide = () => {
  const { pathname } = useLocation();
  const { isOpen, closeGuide } = useGuideStore();
  const [helpQuery, setHelpQuery] = useState('');
  const strategyId = useMemo(() => getStrategyIdFromPath(pathname), [pathname]);
  const { data, isLoading, isError, refetch } = useGuideContext(pathname, strategyId);

  useEffect(() => {
    if (isOpen) void refetch();
  }, [isOpen, refetch]);

  const followSuggestion = (suggestion: GuideSuggestion) => {
    if (suggestion.authorisedRoute === 'guide://current-page-tour') {
      window.open(getOnboardingKnowledgeBaseLink(pathname), '_blank', 'noopener,noreferrer');
      return;
    }
    window.location.assign(suggestion.authorisedRoute);
  };

  const searchHelp = (event: FormEvent) => {
    event.preventDefault();
    const query = helpQuery.trim();
    if (!query) return;
    window.open(`${knowledgeBaseSearch}${encodeURIComponent(query)}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Drawer
        anchor='right'
        open={isOpen}
        onClose={closeGuide}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, bgcolor: '#FCFCF9' } }}
      >
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: '#F7E7D6',
                color: '#A84D0A'
              }}
            >
              <AutoAwesomeIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant='h6' fontWeight={700}>
                weavr Guide
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Guidance based on your role and current work
              </Typography>
            </Box>
            <IconButton aria-label='Close the weavr Guide' onClick={closeGuide}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          {isLoading && (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
              <CircularProgress size={32} />
            </Box>
          )}
          {isError && (
            <Alert severity='info'>
              The Guide could not load your current suggestions. You can still search weavr help below.
            </Alert>
          )}

          {!isLoading && (
            <>
              <Typography variant='h5' fontWeight={700} sx={{ lineHeight: 1.25 }}>
                {getGuideHeading(data)}
              </Typography>
              <Typography color='text.secondary' sx={{ mt: 1, mb: 2.5 }}>
                {getGuideIntro(data)}
              </Typography>

              {data?.member.isReadOnlyImpersonation && (
                <Alert severity='warning' sx={{ mb: 2 }}>
                  You are viewing this Member’s experience in read-only mode.
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {data?.suggestions.map(suggestion => (
                  <Paper
                    key={suggestion.ruleId}
                    variant='outlined'
                    component='button'
                    onClick={() => followSuggestion(suggestion)}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      textAlign: 'left',
                      bgcolor: '#FFFFFF',
                      cursor: 'pointer',
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'center',
                      '&:hover': { borderColor: '#C76016', bgcolor: '#FFF9F3' }
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={700}>{suggestion.title}</Typography>
                      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                        {suggestion.shortReason}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon fontSize='small' sx={{ color: '#A84D0A' }} />
                  </Paper>
                ))}
              </Box>
            </>
          )}

          <Box sx={{ mt: 'auto', pt: 3 }}>
            <Divider sx={{ mb: 2.5 }} />
            <Typography fontWeight={700} sx={{ mb: 0.75 }}>
              Something else?
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
              Search the weavr Knowledge Base in your own words.
            </Typography>
            <Box component='form' onSubmit={searchHelp} sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                size='small'
                value={helpQuery}
                onChange={event => setHelpQuery(event.target.value)}
                placeholder='What do you want help with?'
                inputProps={{ 'aria-label': 'Search weavr help' }}
              />
              <Button type='submit' variant='contained' aria-label='Search weavr help' disabled={!helpQuery.trim()}>
                <SearchIcon />
              </Button>
            </Box>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 2 }}>
              Suggestions only use work and settings you are allowed to access.
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default WeavrGuide;
